import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { whatsappLink } from '@/data/site';
import { EASE } from '@/lib/experience';
import { Paw, WhatsAppIcon } from './brand/Shapes';

const MESSAGE = 'Olá! Vim pelo site da Cãopanhia Pet e gostaria de mais informações 🐾';

/**
 * Botão flutuante do WhatsApp — aparece depois do hero e some quando o
 * bloco de contato (que já tem o botão grande) ou o rodapé estão na tela.
 */
export const FloatingWhatsApp = () => {
  const [scrolled, setScrolled] = useState(false);
  const [contactVisible, setContactVisible] = useState(false);
  const [showBubble, setShowBubble] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > window.innerHeight * 0.6);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const els = [document.getElementById('contato'), document.querySelector('footer')].filter(Boolean);
    const visible = new Set();
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => (e.isIntersecting ? visible.add(e.target) : visible.delete(e.target)));
        setContactVisible(visible.size > 0);
      },
      { threshold: 0.15 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  // o balãozinho só aparece quando a pessoa chega nos preços, e some sozinho ao rolar
  const [reachedPrices, setReachedPrices] = useState(false);
  useEffect(() => {
    const el = document.getElementById('banho');
    if (!el) return undefined;
    const io = new IntersectionObserver(([entry]) => entry.isIntersecting && setReachedPrices(true), { threshold: 0.25 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (dismissed || !reachedPrices) return undefined;
    const show = setTimeout(() => setShowBubble(true), 2500);
    const hide = setTimeout(() => {
      setShowBubble(false);
      setDismissed(true);
    }, 11000);
    return () => {
      clearTimeout(show);
      clearTimeout(hide);
    };
  }, [dismissed, reachedPrices]);

  useEffect(() => {
    if (!showBubble) return undefined;
    const start = window.scrollY;
    const onScroll = () => {
      if (Math.abs(window.scrollY - start) > 350) {
        setShowBubble(false);
        setDismissed(true);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [showBubble]);

  const dismiss = () => {
    setShowBubble(false);
    setDismissed(true);
  };

  const visible = scrolled && !contactVisible;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="fixed z-[70] right-4 bottom-[max(1rem,env(safe-area-inset-bottom))] md:right-6 md:bottom-6 flex flex-col items-end gap-3"
          initial={{ opacity: 0, y: 30, scale: 0.8 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 30, scale: 0.8 }}
          transition={{ duration: 0.5, ease: EASE }}
        >
          <AnimatePresence>
            {showBubble && !dismissed && (
              <motion.div
                className="hidden md:block relative max-w-[250px] rounded-3xl rounded-br-md bg-white text-pet-ink font-bold shadow-2xl px-4 py-3 pr-9 ring-2 ring-pet-pink/60"
                initial={{ opacity: 0, y: 10, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.9 }}
                transition={{ duration: 0.4, ease: EASE }}
              >
                <p className="text-sm leading-snug flex gap-2">
                  <Paw className="w-4 h-4 mt-0.5 shrink-0 text-pet-pink-deep" />
                  Olá! 👋 Quer agendar um banho ou tirar uma dúvida? Estamos aqui!
                </p>
                <button
                  type="button"
                  onClick={dismiss}
                  aria-label="Fechar mensagem"
                  className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full hover:bg-pet-cream flex items-center justify-center text-pet-muted text-lg leading-none"
                >
                  ×
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          <a
            href={whatsappLink(MESSAGE)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="floating-whatsapp"
            aria-label="Falar no WhatsApp"
            onClick={dismiss}
            className="relative group"
          >
            <span className="absolute inset-0 rounded-full bg-[#25D366] opacity-50 animate-ping motion-reduce:hidden" />
            <span className="relative flex items-center justify-center w-14 h-14 md:w-16 md:h-16 rounded-full bg-[#25D366] text-white shadow-[0_18px_40px_-12px_rgba(37,211,102,0.7)] transition-transform duration-300 group-hover:scale-105 group-active:scale-95">
              <WhatsAppIcon className="w-7 h-7 md:w-8 md:h-8" />
            </span>
          </a>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default FloatingWhatsApp;
